import React, { useState, useMemo, useEffect } from "react";
import { Printer, Download, Info, ChevronLeft, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SimpleInfo = ({ label, value }) => (
  <div className="px-4 py-3">
    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-heading font-bold">{label}</p>
    <p className="text-sm font-semibold text-slate-800 font-body">{value}</p>
  </div>
);

export default function TaxComputationMain() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("computation");
  const [showNotes, setShowNotes] = useState(false);
  const [signature, setSignature] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Auto-reset submit button after 5 seconds
  useEffect(() => {
    let timer;
    if (isSubmitted) {
      timer = setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [isSubmitted]);

  const monthOptions = useMemo(() => {
    const options = [];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    [2025, 2026].forEach(year => {
      months.forEach((m, i) => options.push({ label: `${m} ${year}`, value: `${year}-${i + 1}` }));
    });
    return options;
  }, []);

  const taxData = [
    { label: "Basic", value: 654901 },
    { label: "House Rent Allowance", value: 327451 },
    { label: "Special Allowance", value: 618074 },
    { label: "Annual Child Allowance", value: 1500 },
    { label: "Annual H2 Bonus", value: 183046 },
    { label: "Gross Income", value: 1784972, bold: true },
    { label: "Salary After Section 10", value: 1784972 },
    { label: "Standard Deduction", value: 75000 },
    { label: "Sec 16 Exemption", value: 75000 },
    { label: "Salary After Section 16", value: 1709972, bold: true },
    { label: "Taxable Salary", value: 1709980 },
    { label: "Taxable Income", value: 1709980, bold: true },
    { label: "Tax on Total Income", value: 141996 },
    { label: "Tax After Rebate", value: 141996 },
    { label: "Tax After Surcharge", value: 141996 },
    { label: "CESS", value: 5680 },
    { label: "Total Tax For The Year", value: 147676, bold: true },
    { label: "Tax Deducted Till Last Month", value: 170271 },
    { label: "Balance Tax Payable", value: -22595, highlight: true },
  ];

  const handlePrint = () => window.print();

  // PDF Download functionality using browser print engine for styling accuracy
  const handleDownload = () => {
    // Ensure we are on computation tab for the specific download
    if (activeTab !== "computation") {
      setActiveTab("computation");
      // Small timeout to allow state to render before printing
      setTimeout(() => window.print(), 100);
    } else {
      window.print();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6 font-body text-slate-900">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@400;500;700&family=Dancing+Script:wght@600&display=swap');
          .font-heading { font-family: 'Poppins', sans-serif; }
          .font-body { font-family: 'Roboto', sans-serif; }
          .font-signature { font-family: 'Dancing Script', cursive; }
          
          @media print { 
            .no-print { display: none !important; } 
            body { background: white !important; padding: 0 !important; }
            .print-container { padding: 20px !important; }
            .tax-card { border: 1px solid #e2e8f0 !important; page-break-inside: avoid; }
          }
        `}
      </style>

      <div className="max-w-5xl mx-auto print-container">
        {/* Navigation & Header */}
        <div className="flex flex-col md:flex-row justify-between items-start mb-4 no-print gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 bg-white border rounded-full hover:bg-slate-100 transition-all shadow-sm">
              <ChevronLeft size={18} />
            </button>
            <h2 className="text-xl font-bold font-heading text-slate-800">Tax Computation</h2>
          </div>

          <div className="flex items-center gap-2">
            <select className="border border-slate-300 rounded px-2 py-1 text-xs bg-white focus:outline-none">
              {monthOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <button onClick={handlePrint} className="flex items-center gap-1 px-3 py-1 bg-white border border-slate-300 rounded text-[11px] font-semibold hover:bg-slate-50 transition-all">
              <Printer size={13} /> Print
            </button>
            <button onClick={handleDownload} className="flex items-center gap-1 px-3 py-1 bg-emerald-600 text-white rounded text-[11px] font-semibold hover:bg-emerald-700 transition-all">
              <Download size={13} /> Download PDF
            </button>
            <button 
                onClick={() => setActiveTab(activeTab === "computation" ? "declaration" : "computation")} 
                className="px-3 py-1 bg-blue-600 text-white rounded text-[11px] font-semibold hover:bg-blue-700 transition-all ml-2"
            >
              {activeTab === "computation" ? "Tax Declarations" : "Show Computation"}
            </button>
          </div>
        </div>

        {/* Employee Info */}
        <div className="bg-white border border-slate-200 rounded-lg mb-4 shadow-sm tax-card">
          <div className="grid grid-cols-2 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-slate-100">
            <SimpleInfo label="Emp Code" value="EMP001" />
            <SimpleInfo label="Name" value="Ravi Kumar" />
            <SimpleInfo label="Joined" value="15/03/2022" />
            <SimpleInfo label="Designation" value="Sr. Dev" />
            <SimpleInfo label="Regime" value="New" />
          </div>
        </div>

        {activeTab === "computation" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 animate-in fade-in duration-300">
            {taxData.map((item, index) => (
              <div key={index} className={`flex justify-between items-center p-3 border rounded-lg bg-white tax-card ${item.highlight ? "border-red-200 bg-red-50" : "border-slate-100"}`}>
                <span className={`text-xs font-body ${item.bold ? "font-bold text-slate-900" : "text-slate-500"}`}>{item.label}</span>
                <span className={`text-sm font-bold font-body ${item.highlight ? "text-red-600" : "text-slate-800"}`}>
                  ₹{item.value.toLocaleString("en-IN")}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4 animate-in slide-in-from-bottom-2 duration-300">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm tax-card">
              <div className="flex justify-between items-center p-4 border-b no-print">
                <h1 className="text-sm font-bold font-heading text-slate-700">Tax Declarations</h1>
                <button
                  onClick={() => setShowNotes(true)}
                  className="px-3 py-1 bg-blue-600 text-white text-[10px] font-bold rounded hover:bg-blue-700 uppercase tracking-wider"
                >
                  Declaration Notes
                </button>
              </div>

              <div className="p-6 text-xs text-slate-700 font-body">
                <p className="mb-8 leading-relaxed italic">
                  I certify that the declaration listed above has been made by me out of my income
                  chargeable to income tax during the financial year 2025 - 2026.
                  In case proof of payment is not provided within the required due date of submission
                  (as notified to me), then income tax can be deducted as applicable.
                </p>

                <div className="grid md:grid-cols-2 gap-8 items-end border-t pt-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">Signature :</span>
                      <input 
                        type="text" 
                        value={signature} 
                        onChange={(e) => setSignature(e.target.value)}
                        className="border-b border-slate-300 py-0.5 outline-none focus:border-blue-500 transition-colors w-40 font-signature text-lg text-blue-800 no-print"
                        placeholder="Type to sign..."
                      />
                      <span className="hidden print:inline font-signature text-lg text-blue-800 border-b border-slate-300 min-w-[150px]">{signature}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">Date :</span>
                      <span className="font-medium">23-Dec-2025</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 justify-end no-print">
                    <div className="w-32 p-1.5 border border-slate-100 bg-slate-50 rounded text-center">
                       <p className="text-[7px] text-slate-400 uppercase mb-0.5 font-bold">Preview</p>
                       <p className="font-signature text-base text-blue-900 h-5 truncate">
                         {signature || "..."}
                       </p>
                    </div>

                    <button 
                      disabled={!signature || isSubmitted}
                      onClick={() => setIsSubmitted(true)}
                      className={`px-4 py-1.5 rounded text-[10px] font-bold transition-all uppercase tracking-widest ${
                        isSubmitted 
                        ? 'bg-emerald-100 text-emerald-700 flex items-center gap-1' 
                        : 'bg-slate-900 text-white hover:bg-slate-800 shadow-sm active:scale-95'
                      }`}
                    >
                      {isSubmitted ? <><CheckCircle size={10}/> Submitted</> : 'Submit'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Declaration Notes Modal */}
        {showNotes && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 no-print">
            <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden border border-slate-200">
              <div className="flex justify-between items-center px-4 py-3 border-b bg-slate-50">
                <h2 className="font-bold text-slate-700 font-heading text-xs uppercase tracking-wider">
                  Tax Declaration - Entitlements
                </h2>
                <button onClick={() => setShowNotes(false)} className="text-slate-400 hover:text-slate-800 text-xl transition-colors">×</button>
              </div>

              <div className="p-5 text-[11px] text-slate-600 space-y-3 font-body leading-relaxed max-h-[60vh] overflow-y-auto">
                <p>➤ Employee contribution to Provident Fund if any, will be considered u/s 80C by default. Please take note of this while declaring your investments.</p>
                <p>➤ Form 12C Declarations (Income other than Salaries) require scanned signatures.</p>
                <p>➤ Fixed Deposits with 5+ year lock-in are eligible for declaration.</p>
                <p>➤ All declarations impact subsequent payouts retrospectively.</p>
              </div>

              <div className="p-3 border-t bg-slate-50 text-right">
                <button onClick={() => setShowNotes(false)} className="px-4 py-1.5 bg-white border border-slate-200 rounded text-[10px] font-bold text-slate-700 hover:bg-slate-100 uppercase transition-all shadow-sm">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}